/**
 * Which pool entry the nth speaking picks — D30's sequential cursor and
 * D141's shuffled one, behind one function.
 *
 * @remarks
 * **Sequential (D30):** the nth speaking picks the nth entry and holds on the
 * last — a line that escalates and then settles.
 *
 * **Shuffled (D141):** every entry once per CYCLE, in a permutation the
 * player cannot predict, a fresh permutation each cycle whose first entry
 * never repeats the one just heard. The drips and the rats: a pool of two or
 * three where "not the same line twice in a row" is the whole feature.
 *
 * **Why the permutation is DERIVED rather than drawn and stored.** The
 * session's randomness stream is threaded through every turn and had never
 * been drawn; the obvious design draws from it and stores the permutation as
 * pool state. Deriving it instead — a local generator seeded from
 * `(session seed, line id, cycle)` — buys three things at once: it is
 * replay-safe by construction (same seed, same commands, same order); it
 * survives a save WITHOUT being saved, because the seed and the usage count
 * are already state (Calder's D139 probe, answered before it is written); and
 * it is order-independent — adding a second random consumer elsewhere in the
 * turn cannot reshuffle the rats, which a shared stream would allow. The
 * global stream stays for the things that must be order-dependent.
 *
 * @packageDocumentation
 */

import type { VoiceLineDefinition } from '@orpheus/content-schema';

import { createRandomnessState, drawIntegerBetween } from '../rng/random.js';
import { fnv1a64 } from '../saves/canonical-json.js';

/**
 * One element of an array at an index the caller has already bounded.
 *
 * @remarks
 * `noUncheckedIndexedAccess` types every index read `number | undefined`, and
 * every read in this file is of an index the surrounding arithmetic has
 * already kept in range: a position taken modulo the pool's length, a
 * Fisher–Yates draw bounded by the loop, the first two of a pool the caller
 * has just checked has at least two. Written inline, that is five `?? 0`
 * fallbacks, five unreachable branches, and five places for a future reader to
 * wonder whether the zero means something. Written here it is ONE — named,
 * argued for once, and the only line in the file a coverage exemption touches.
 *
 * The fallback is `0` rather than a throw because this runs inside a turn: a
 * narrative that picks the wrong pool line is a blemish, and a narrative that
 * throws is a dead session.
 *
 * Verified 2026-09-14: every other branch in this file is covered, and this
 * one cannot be entered without an out-of-range index.
 *
 * @param values - The array
 * @param index - An index the caller has bounded
 * @returns The element
 */
const boundedElement = (values: readonly number[], index: number): number => {
	const value = values[index];

	/* v8 ignore next 3 */
	if (value === undefined) {
		return 0;
	}

	return value;
};

/**
 * The pool index the nth speaking of this line picks.
 *
 * @param definition - The line, with its pool and order
 * @param timesSpoken - How many times the line has spoken before this one
 * @param sessionSeed - The session's randomness seed (the shuffle's root)
 * @returns An index into `definition.pool`
 */
export const poolIndexFor = (definition: VoiceLineDefinition, timesSpoken: number, sessionSeed: number): number => {
	const poolSize = definition.pool.length;

	if (definition.poolOrder === 'sequential') {
		return Math.min(timesSpoken, poolSize - 1);
	}

	const cycle = Math.floor(timesSpoken / poolSize);
	const position = timesSpoken % poolSize;

	return boundedElement(permutationFor(definition, cycle, sessionSeed), position);
};

/**
 * The cycle's permutation of pool indices: the raw Fisher–Yates shuffle,
 * with the seam rule applied — a cycle after the first never opens with the
 * entry the previous cycle closed on, so no line is heard twice in a row
 * across the seam.
 *
 * The previous cycle's LAST entry is read without recursion: the seam swap
 * touches positions 0 and 1 only, so for a pool of three or more the last
 * entry is the raw shuffle's last; for a pool of two the swap is the whole
 * permutation, and the rule collapses into strict alternation — every cycle
 * of a two-line pool is the first cycle's order, which is the only order that
 * never repeats across a seam.
 *
 * @param definition - The line
 * @param cycle - Which full pass through the pool this is (0-based)
 * @param sessionSeed - The session's randomness seed
 * @returns Every pool index exactly once
 */
const permutationFor = (definition: VoiceLineDefinition, cycle: number, sessionSeed: number): readonly number[] => {
	const indices = [...rawShuffle(definition, cycle, sessionSeed)];

	if (cycle > 0) {
		const previousLast =
			definition.pool.length === 2 ? rawShuffle(definition, 0, sessionSeed)[1] : rawShuffle(definition, cycle - 1, sessionSeed).at(-1);

		if (indices[0] === previousLast) {
			[indices[0], indices[1]] = [boundedElement(indices, 1), boundedElement(indices, 0)];
		}
	}

	return indices;
};

/**
 * Fisher–Yates over a generator seeded from the session, the line, and the
 * cycle — the same inputs, the same order, on every machine and every reload.
 *
 * @param definition - The line
 * @param cycle - The cycle number
 * @param sessionSeed - The session's randomness seed
 * @returns Every pool index exactly once, before the seam rule
 */
const rawShuffle = (definition: VoiceLineDefinition, cycle: number, sessionSeed: number): readonly number[] => {
	const indices = Array.from({ length: definition.pool.length }, (_, index) => index);
	let randomness = createRandomnessState(seedFor(definition, cycle, sessionSeed));

	for (let position = indices.length - 1; position > 0; position -= 1) {
		const draw = drawIntegerBetween(randomness, 0, position);

		randomness = draw.nextState;
		[indices[position], indices[draw.value]] = [boundedElement(indices, draw.value), boundedElement(indices, position)];
	}

	return indices;
};

/**
 * A 32-bit seed unique to (session, line, cycle) — the low bits of FNV-1a
 * over their names.
 *
 * @param definition - The line
 * @param cycle - The cycle number
 * @param sessionSeed - The session's randomness seed
 * @returns A seed for {@link createRandomnessState}
 */
const seedFor = (definition: VoiceLineDefinition, cycle: number, sessionSeed: number): number =>
	Number.parseInt(fnv1a64(`${String(sessionSeed)}:${definition.voiceLineId}:${String(cycle)}`).slice(-8), 16);
